import React, { useMemo, useState } from 'react';
import { FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Typography, Alert, Checkbox } from '@mui/material';
import countryList from 'react-select-country-list';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createSenderId, saveSMTPSettings } from '../../../Services';
import images from '../../../Utils/images';
import CheerioButton from '../../../Components/CheerioButton';
import colors from '../../../Utils/colors';

const SetupEmail = () => {
  const auth_token = useSelector((state) => state.main.auth_token);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('sender');
  const [data, setData] = useState({
    name: '', // sender name
    email: '', // sender email
    replier_name: '', // reply-to name
    replier_email: '', // reply-to email
    nick_name: '',
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });

  const [settingsData, setSettingsData] = useState({
    messagePerDay: '',
    minGapTime: '',
    replyTo: '',
    senderName: '',
    emailSignature: '',
    emailWarmup: {
      emailPerDay: '',
      dropRate: '',
      isActive: false,
    },
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [emailVerified, setEmailVerified] = useState(false);

  const WARMUP_LIMITS = {
    emailPerDay: { min: 2, max: 50, recommended: { min: 20, max: 50 } },
    replyRate: { min: 0, max: 100, recommended: { min: 20, max: 40 } }
  };

  const SMTP_LIMITS = {
    messagePerDay: { min: 1, max: 10000 },
    minGapTime: { min: 1, max: 1440 } // 1 minute to 24 hours
  };

  const validateName = (name, fieldName) => {
    if (!name.trim()) return '';
    if (name.length < 2) return `${fieldName} must be at least 2 characters`;
    if (name.length > 100) return `${fieldName} must be less than 100 characters`;
    if (!/^[a-zA-Z\s'-]+$/.test(name)) return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    return '';
  };

  const validateAddress = () => {
    const errors = {};

    if (!data.country) errors.country = 'Country is required';
    if (!data.city || data.city.length < 2) errors.city = 'Valid city name required';
    if (!data.zip_code || data.zip_code.length < 3) errors.zip_code = 'Valid postal code required';
    if (!data.address || data.address.length < 5) errors.address = 'Complete address required (minimum 5 characters)';

    return errors;
  };

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setData((prev) => ({ ...prev, [key]: value }));

    // Validate names
    if (key === 'name' || key === 'replier_name') {
      const error = validateName(value, key === 'name' ? 'Sender name' : 'Reply-to name');
      setValidationErrors(prev => ({ ...prev, [key]: error }));
    }

    // Validate emails
    if (key === 'email' || key === 'replier_email') {
      const error = value && !emailRegex.test(value) ? 'Enter a valid email address' : '';
      setValidationErrors(prev => ({ ...prev, [key]: error }));
    }
  };

  const onSettingsChange = (key, toNumber = false) => (e) => {
    const v = e.target.value;
    const numValue = toNumber ? (v === '' ? '' : Number(v)) : v;

    setSettingsData((prev) => ({
      ...prev,
      [key]: numValue,
    }));

    // Validate SMTP fields
    if (toNumber && v !== '') {
      let error = '';
      if (key === 'messagePerDay') {
        if (numValue < SMTP_LIMITS.messagePerDay.min) {
          error = `Minimum ${SMTP_LIMITS.messagePerDay.min} message per day`;
        } else if (numValue > SMTP_LIMITS.messagePerDay.max) {
          error = `Maximum ${SMTP_LIMITS.messagePerDay.max} messages per day`;
        }
      } else if (key === 'minGapTime') {
        if (numValue < SMTP_LIMITS.minGapTime.min) {
          error = `Minimum ${SMTP_LIMITS.minGapTime.min} minute gap required`;
        } else if (numValue > SMTP_LIMITS.minGapTime.max) {
          error = `Maximum ${SMTP_LIMITS.minGapTime.max} minutes (24 hours)`;
        }
      }

      setValidationErrors(prev => ({
        ...prev,
        [key]: error
      }));
    }
  };

  const validateCurrentStep = () => {
    let errors = {};

    if (currentStep === 'sender') {
      if (!data.name) errors.name = 'Sender name is required';
      if (!data.email) errors.email = 'Sender email is required';
      if (data.email && !emailRegex.test(data.email)) errors.email = 'Invalid email format';
      if (!data.replier_name) errors.replier_name = 'Reply-to name is required';
      if (!data.replier_email) errors.replier_email = 'Reply-to email is required';
      if (data.replier_email && !emailRegex.test(data.replier_email)) errors.replier_email = 'Invalid email format';
    } else if (currentStep === 'address') {
      errors = { ...errors, ...validateAddress() };
    } else if (currentStep === 'smtp') {
      if (!settingsData.messagePerDay || settingsData.messagePerDay < 1) {
        errors.messagePerDay = 'Email sending limit is required';
      }
      if (!settingsData.minGapTime || settingsData.minGapTime < 1) {
        errors.minGapTime = 'Minimum gap time is required';
      }
    } else if (currentStep === 'warmup') {
      if (settingsData.emailWarmup.isActive) {
        if (!settingsData.emailWarmup.emailPerDay) {
          errors.warmup_emailPerDay = 'Warmup emails per day is required';
        }
        if (settingsData.emailWarmup.dropRate === '') {
          errors.warmup_dropRate = 'Reply rate is required';
        }
      }
    } else if (currentStep === 'verify') {
      if (!emailVerified) {
        errors.verification = 'Please verify your email before proceeding';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onWarmupChange = (key, toNumber = false) => (e) => {
    const v = e.target.value;
    const numValue = toNumber ? (v === '' ? '' : Number(v)) : v;

    setSettingsData((prev) => ({
      ...prev,
      emailWarmup: {
        ...prev.emailWarmup,
        [key]: numValue,
      },
    }));

    // Validate warmup fields
    if (toNumber && v !== '') {
      let error = '';
      if (key === 'emailPerDay') {
        if (numValue < WARMUP_LIMITS.emailPerDay.min) {
          error = `Minimum ${WARMUP_LIMITS.emailPerDay.min} emails per day required`;
        } else if (numValue > WARMUP_LIMITS.emailPerDay.max) {
          error = `Maximum ${WARMUP_LIMITS.emailPerDay.max} emails per day allowed`;
        }
      } else if (key === 'dropRate') {
        if (numValue < WARMUP_LIMITS.replyRate.min) {
          error = `Minimum ${WARMUP_LIMITS.replyRate.min}% reply rate`;
        } else if (numValue > WARMUP_LIMITS.replyRate.max) {
          error = `Maximum ${WARMUP_LIMITS.replyRate.max}% reply rate`;
        }
      }

      setValidationErrors(prev => ({
        ...prev,
        [`warmup_${key}`]: error
      }));
    }
  };

  const toggleWarmup = (e) => {
    const checked = e.target.checked;
    setSettingsData((prev) => ({
      ...prev,
      emailWarmup: { ...prev.emailWarmup, isActive: checked },
    }));
  };

  const warmupDisabled = !settingsData.emailWarmup.isActive;

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);
  const emailErrorSender = data.email && !emailRegex.test(data.email);
  const emailErrorReply = data.replier_email && !emailRegex.test(data.replier_email);

  const options = useMemo(() => countryList().getData(), []);

  const createSenderIdApiCall = async () => {
    let token = auth_token;
    try {
      const res = await createSenderId(data, token);
      if (res?.flag) {
        toast.success(res?.message, { position: 'top-center', autoClose: true, closeButton: true });
        return true;
      } else {
        toast.error(res?.message ?? 'Failed to create Sender ID', { position: 'top-center', autoClose: true, closeButton: true });
        return false;
      }
    } catch (error) {
      toast.error('Unexpected error occurred while creating Sender ID', { position: 'top-center', autoClose: true, closeButton: true });
      return false;
    }
  };


  const saveSettingsAction = () => {
    let token = auth_token;
    let apiData = {
      email: data.email,
      setting: settingsData,
    };
    saveSMTPSettings(apiData, token).then((res) => {
      if (res?.flag) {
        toast.success('Successfully saved settings', { position: 'top-right', autoClose: true, closeButton: true });
      } else {
        toast.error(res?.message ?? 'Unexpected error occurred, please try again later', { position: 'top-right', autoClose: true, closeButton: true });
      }
    });
  };

  return (
    <>
      <div className="setup_container">
        <div className="setup_container__left verythinscrollnewinv">
          <div className="setup_container__left__header">
            <img
              src={images.BackArrowBlack}
              style={{ height: 20, width: 20, objectFit: 'contain', cursor: 'pointer' }}
              onClick={() => {
                navigate(-1);
              }}
            />
            <h3>{'Email on Omnybox'}</h3>
          </div>

          <h4>{`What you'll need`}</h4>
          <h4>{`Pre-installation`}</h4>
          <p>
            {`To install Email on Omnybox, you need to have an email ID with a custom domain. You are required to setup a sender ID for every promotional email you send in order to comply with the anti- spam laws such as CAN-SPAM and CASL.`}
          </p>
          <h4>{`What can I do with Email on Omnybox?`}</h4>
          <p>
            {`1. Use email to send your customers hyper-targeted, behavior-triggered, and eye-catching emails in just a few minutes.`}
            <br />
            {`2. Use our sign-up forms on your web pages to build your email audience, and keep customers coming back with conversational email nurturing flows.`}
            <br />
            {`3. Email Verification: Verify your recipients data.`}
            <br />
            {`4. Warmup your emails automatically to improve email delivery and domain health.`}
            <br />
            {`5. Create automated email sequences for rich follow ups and conversions.`}
            <br />
            {`6. Automate emails on custom event based triggers via webhooks.`}
          </p>
          <h4>{`Channel Capabilities`}</h4>
          <p>
            {`1. Text`}
            <br />
            {`2. Image`}
            <br />
            {`3. Links`}
            <br />
            {`4. Button`}
            <br />
            {`5. Custom HTML`}
          </p>
        </div>
        <div className="setup_container__right">
          <div className="setup_modal">
            <h3>
              {currentStep === 'sender'
                ? `Add Sender Details`
                : currentStep === 'address'
                  ? `Add Address Details`
                  : currentStep === 'smtp'
                    ? `Setup SMTP Settings`
                    : currentStep === 'warmup'
                      ? `Setup Warmup Settings`
                      : currentStep === 'verify'
                        ? `Verify Email via link`
                        : currentStep === 'success'
                          ? `Sender ID Created`
                          : ``}
            </h3>

            {/* Subtitle for Address step */}
            {currentStep === 'address' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                To Maintain a healthy ecosystem and authenticity of your domain and sender ID. We require these details
              </Typography>
            )}

            {/* Info banner for SMTP and Warmup steps */}
            {(currentStep === 'smtp' || currentStep === 'warmup') && (
              <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                You can edit these settings later
              </Alert>
            )}

            <div className="setup_modal__form">
              {currentStep === 'sender' ? (
                <>
                  <TextField
                    fullWidth
                    label="Sender Name"
                    value={data.name}
                    onChange={onChange('name')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    error={!!validationErrors.name}
                    helperText={validationErrors.name || 'The name your recipient will receive on their email'}
                  />
                  <TextField
                    fullWidth
                    label="Sender Email"
                    value={data.email}
                    onChange={onChange('email')}
                    error={!!validationErrors.email}
                    helperText={validationErrors.email || 'This email ID will be used to send emails to your recipients'}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth
                    label="Reply to Name"
                    value={data.replier_name}
                    onChange={onChange('replier_name')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    error={!!validationErrors.replier_name}
                    helperText={validationErrors.replier_name || 'The person who will receive the email if your recipient replies'}
                  />
                  <TextField
                    fullWidth
                    label="Reply to Email"
                    value={data.replier_email}
                    onChange={onChange('replier_email')}
                    error={!!validationErrors.replier_email}
                    helperText={validationErrors.replier_email || 'The email ID on which you want to receive emails from your recipients'}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </>
              ) : currentStep === 'address' ? (
                <>
                  <FormControl fullWidth className="my-2">
                    <InputLabel>Country</InputLabel>
                    <Select
                      label="Country"
                      value={data.country}
                      onChange={onChange('country')}
                      sx={{ borderRadius: 2 }}
                      error={!!validationErrors.country}
                    >
                      <MenuItem value="" disabled>
                        <em>Choose country</em>
                      </MenuItem>

                      {(options || []).map((option, index) => (
                        <MenuItem key={option?.value ?? index} value={option?.value ?? option?.label ?? ''}>
                          {option?.label ?? option?.value}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Country where you'll be sending these emails from
                    </Typography>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="City"
                    value={data.city}
                    onChange={onChange('city')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    error={!!validationErrors.city}
                    helperText={validationErrors.city || "City where you'll be sending these emails from"}
                  />
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={data.zip_code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                      setData((p) => ({ ...p, zip_code: v }));
                    }}
                    inputMode="numeric"
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    error={!!validationErrors.zip_code}
                    helperText={validationErrors.zip_code || "Pincode where you'll be sending these emails from"}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    value={data.address}
                    onChange={onChange('address')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    error={!!validationErrors.address}
                    helperText={validationErrors.address || "Address where you'll be sending these emails from"}
                  />
                  <TextField
                    fullWidth
                    label="Nickname"
                    value={data.nick_name}
                    onChange={onChange('nick_name')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    helperText="Add a Nickname for your reference"
                  />
                </>
              ) : currentStep === 'smtp' ? (
                <>
                  <TextField
                    fullWidth
                    label="Email sending limit per day"
                    value={settingsData.messagePerDay}
                    onChange={onSettingsChange('messagePerDay', true)}
                    type="number"
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      min: SMTP_LIMITS.messagePerDay.min,
                      max: SMTP_LIMITS.messagePerDay.max
                    }}
                    error={!!validationErrors.messagePerDay}
                    helperText={validationErrors.messagePerDay || 'You can limits to number of emails you want to send from this sender ID. This helps maintain domain and sender ID health'}
                  />
                  <TextField
                    fullWidth
                    label="Minimum gap time (Mins)"
                    value={settingsData.minGapTime}
                    onChange={onSettingsChange('minGapTime', true)}
                    type="number"
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      min: SMTP_LIMITS.minGapTime.min,
                      max: SMTP_LIMITS.minGapTime.max
                    }}
                    error={!!validationErrors.minGapTime}
                    helperText={validationErrors.minGapTime || 'The gap time between two consecutive emails. This helps maintain sender ID health and avoids spamming'}
                  />
                  <TextField
                    fullWidth
                    label="Add email signature"
                    value={settingsData.emailSignature}
                    onChange={onSettingsChange('emailSignature')}
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    multiline
                    minRows={4}
                  />
                </>
              ) : currentStep === 'warmup' ? (
                <>
                  <div className="my-2 Row justify-content-start w-100">
                    <Typography variant="subtitle2" className="fw-bold mb-1">
                      Email Warmup
                    </Typography>

                    <FormControlLabel
                      className="mb-2 ms-2"
                      control={<Switch size="small" checked={settingsData.emailWarmup.isActive} onChange={toggleWarmup} />}
                      label=""
                    />
                  </div>
                  <TextField
                    fullWidth
                    label="Warmup emails per day"
                    value={settingsData.emailWarmup.emailPerDay}
                    onChange={onWarmupChange('emailPerDay', true)}
                    type="number"
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      min: WARMUP_LIMITS.emailPerDay.min,
                      max: WARMUP_LIMITS.emailPerDay.max,
                      step: 1
                    }}
                    disabled={warmupDisabled}
                    error={!!validationErrors.warmup_emailPerDay}
                    helperText={
                      validationErrors.warmup_emailPerDay ||
                      `No. of warmup emails exceed your daily emails you want to send. Recommended limit (20-50 emails)`
                    }
                  />
                  <TextField
                    fullWidth
                    label="Reply rate(%)"
                    value={settingsData.emailWarmup.dropRate}
                    onChange={onWarmupChange('dropRate', true)}
                    type="number"
                    variant="outlined"
                    className="my-2"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    inputProps={{
                      min: WARMUP_LIMITS.replyRate.min,
                      max: WARMUP_LIMITS.replyRate.max,
                      step: 1
                    }}
                    disabled={warmupDisabled}
                    error={!!validationErrors.warmup_dropRate}
                    helperText={
                      validationErrors.warmup_dropRate ||
                      `By what percentage you want to receive replies on warmup emails. Recommended (20% - 40%)`
                    }
                  />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Want to know more about warmup?{' '}
                    <a href="#" style={{ color: colors.primary03, textDecoration: 'none' }}>
                      Learn more
                    </a>
                  </Typography>
                </>
              ) : currentStep === 'verify' ? (
                <>
                  <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
                    Check your spam, if not found.
                  </Alert>

                  <div className="Column w-100 my-3" style={{ alignItems: 'center' }}>
                    {/* Illustration */}
                    <img
                      src={images.SenderVerifyImage}
                      style={{
                        width: 180,
                        height: 180,
                        objectFit: 'contain',
                        marginBottom: 24
                      }}
                      alt="Email Verification"
                    />

                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: 'center',
                        mb: 3,
                        fontWeight: 600,
                        fontSize: '1.25rem'
                      }}
                    >
                      Verify Email via link
                    </Typography>

                    {/* Email Screenshot */}
                    <div style={{
                      width: '100%',
                      maxHeight: '300px',
                      overflow: 'auto',
                      marginBottom: 24,
                      border: '1px solid #e0e0e0',
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <img
                        src={images.VerificationScreenshot}
                        style={{
                          width: '100%',
                          borderRadius: 4,
                          objectFit: 'contain',
                          display: 'block'
                        }}
                        alt="Example Amazon SES Verification Email"
                      />
                    </div>

                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: 'center',
                        mb: 3,
                        color: 'text.primary',
                        lineHeight: 1.6
                      }}
                    >
                      You will receive a verification link from amazon web services. Please click on the link to verify your sender ID
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={emailVerified}
                          onChange={(e) => setEmailVerified(e.target.checked)}
                          sx={{
                            '&.Mui-checked': {
                              color: colors.primary03
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          I have verified using the link.
                        </Typography>
                      }
                    />
                    {validationErrors.verification && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {validationErrors.verification}
                      </Typography>
                    )}
                  </div>
                </>
              ) : (
                currentStep === 'success' && (
                  <>
                    <div className="Column w-100 my-3" style={{ alignItems: 'center' }}>
                      {/* Green Checkmark Circle */}
                      <div style={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        backgroundColor: '#00C853',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 24
                      }}>
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                            fill="white"
                          />
                        </svg>
                      </div>

                      <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        Congratulations, you're sender ID is successfully created and ready to blast out campaigns
                      </Typography>
                    </div>
                  </>
                )
              )}
            </div>
            <CheerioButton
              btnText={currentStep === 'success' ? `Close` : currentStep === 'verify' ? `Done` : `Next`}
              borderStyle={{
                paddingBlock: '10px',
                borderColor: colors.primary03,
                width: '100%',
                opacity: currentStep === 'verify' && !emailVerified ? 0.5 : 1,
                cursor: currentStep === 'verify' && !emailVerified ? 'not-allowed' : 'pointer'
              }}
              textStyle={{ color: colors.white01 }}
              backColor={colors.primary03}
              disabled={currentStep === 'verify' && !emailVerified}
              onclick={async () => {
                // Prevent action if verify step and not verified
                if (currentStep === 'verify' && !emailVerified) {
                  toast.error('Please verify your email before proceeding', {
                    position: 'top-center',
                    autoClose: 3000,
                  });
                  return;
                }

                if (!validateCurrentStep()) {
                  toast.error('Please fix all validation errors before proceeding', {
                    position: 'top-center',
                    autoClose: 3000,
                  });
                  return;
                }

                if (currentStep === 'sender') {
                  setCurrentStep('address');
                } else if (currentStep === 'address') {
                  const success = await createSenderIdApiCall();

                  if (success) setCurrentStep('smtp');
                } else if (currentStep === 'smtp') {
                  setCurrentStep('warmup');
                } else if (currentStep === 'warmup') {
                  saveSettingsAction();
                  setCurrentStep('verify');
                } else if (currentStep === 'verify') {
                  setCurrentStep('success');
                } else if (currentStep === 'success') {
                  navigate(-1); // or navigate to dashboard
                }
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SetupEmail;